import {ResultsRowMetadata, ResultsTableMetadata} from 'notebook/integrations/results/ResultsTableConstants';
import {PendingTableRow} from 'notebook/structured-tables/queries/FETCH_PENDING_TABLE_ROWS_QUERY';
import {
  BaseStructuredTableColumn,
  TableActionFunctionParameters,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {updateLastSyncedUpstreamValues} from 'notebook/tables/refresh-table-data/updateStructuredTableUsingRowUpdater';
import TableUtil from 'notebook/tables/TableUtil';
import {convertTagToTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import notifications from 'notifications';

import addRowsToTable from '../util/addRowsToTable';
import {getMaxRowsConfig} from '../util/checkTableCapacity';

type MutationParams = Pick<
  TableActionFunctionParameters<ResultsTableMetadata, ResultsRowMetadata>,
  'structuredTable' | 'structuredTableAdapter' | 'updateHandler'
> & {
  pendingRows: Array<PendingTableRow>;
  onComplete: () => void;
};

export function addAPIPendingValues({
  pendingRows,
  onComplete,
  structuredTable,
  structuredTableAdapter,
  updateHandler,
}: MutationParams): void {
  if (pendingRows.length === 0) {
    notifications.warn('All API pending values have already been added to this table.');
    const cells = structuredTable.rows.map((row) => row.cells);
    updateHandler({updatedCells: cells, columnUpdates: {}, numApiRowsToAdd: 0});
    return;
  }

  /** In theory, tables will account for the number of pending rows when they limit the number of rows
        that can be added, so a table with a limit of 500 rows that has 2 rows pending should not allow the
        user to extend the table beyond 498 rows. This extra check just makes extra sure the max row limit
        isn't violated, even if the previous checks are circumvented.
        This would also get caught in the addRowsToTable call, but by catching it here we can give a slightly
        more specific error notification, and ensure we're using the right config flag. */
  const maxRows = getMaxRowsConfig(true, structuredTable.kind);
  if (structuredTable.rows.length + pendingRows.length > maxRows) {
    const maxRows = structuredTableAdapter.getTableLengthString(structuredTable, true);
    notifications.error(`Cannot add pending results; table length would exceed max (${maxRows}).`);
    return;
  }

  const addedRows = pendingRows.map((row) =>
    structuredTable.columns.map((column) => convertPendingRowToRow(row, column))
  );
  const newRowMetadatas = pendingRows.map((result) => ({euid: result.id}));
  const addedEuids = newRowMetadatas.map((data) => data.euid);

  const addRowsResult = addRowsToTable(
    addedRows,
    structuredTable,
    structuredTableAdapter,
    newRowMetadatas,
    {},
    addedRows.length // number of rows being added that are considered pending on the structured table
  );

  // If addRowsResult encounters an error, it will notify the user and return null.
  if (addRowsResult == null) {
    return;
  }

  const updatedTable = addRowsResult.updatedTable;
  const updatedLsuvRows = updatedTable.rows.map((row) => {
    if (row.metadata.euid !== undefined && addedEuids.includes(row.metadata.euid)) {
      const updatedRow = updateLastSyncedUpstreamValues(structuredTable.columns, null, row);
      return updatedRow.lastSyncedUpstreamValues ?? [];
    } else {
      return row.lastSyncedUpstreamValues ?? [];
    }
  });

  const updatedCells = updatedTable.rows.map((row) => row.cells);
  const updatedRowMetadatas: Array<ResultsRowMetadata> = updatedTable.rows.map((row) => ({
    rowId: row.rowId,
    isFinalized:
      row.metadata.euid !== undefined && addedEuids.includes(row.metadata.euid)
        ? true
        : (row.isFinalized ?? false),
    euid: row.metadata.euid,
  }));

  updateHandler({
    updatedCells,
    columnUpdates: {},
    updatedRowMetadatas,
    numApiRowsToAdd: 0,
    lastSyncedUpstreamValuesByRow: updatedLsuvRows,
    tableOp: addRowsResult.tableOp,
  });

  onComplete();
}

function convertPendingRowToRow(row: PendingTableRow, column: BaseStructuredTableColumn<{}>): Cell<unknown> {
  if (column.columnType !== TableColumnType.TAG_VALUE) {
    return TableUtil.makeEmptyCell();
  }
  const tag = row.tagsJson.find(({tagSchemaField}) => tagSchemaField.apiId === column.schemaFieldApiId);
  if (!tag) {
    return TableUtil.makeEmptyCell();
  }
  const typedValue = convertTagToTypedValue(tag);
  if (isTypedValueEmpty(typedValue)) {
    return TableUtil.makeEmptyCell();
  }
  return {text: '', typedValue};
}
